import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Hint } from '@/components/Hint'
import { useT } from '@/i18n/I18nProvider'
import { cn, formatNumber } from '@/lib/utils'
import { serializeQueue, tabsFor, updateQueue, type QueueState } from '@/lib/submissionQueue'
import type { SubmissionTabCounts } from '@/types/api'

/**
 * The queues above the list, each with how many reports it holds.
 *
 * Links rather than buttons, because each one *is* an address — a supervisor
 * can open "Van roka" in a second tab or send it to somebody, and the back
 * button walks back through the tabs they looked at.
 *
 * Not the shared `Tabs`: those are 32px segments made for switching panels on
 * a desk, and this row is the first thing a gloved thumb reaches for. The
 * pills use `min-h-control`, the same token as every button.
 */
export function QueueTabs({
  state,
  counts,
  isManager,
}: {
  state: QueueState
  counts: SubmissionTabCounts | undefined
  isManager: boolean
}) {
  const t = useT()
  const navRef = useRef<HTMLElement>(null)

  /*
   * On a phone the row scrolls, and a link straight to "Van roka" opened with
   * the active pill off the right edge — the one thing on the row that says
   * what the list below is. Brought into view without moving the page.
   */
  const counted = counts !== undefined

  // Again once the counts arrive, which widen every pill.
  useEffect(() => {
    const nav = navRef.current
    const active = nav?.querySelector<HTMLElement>('[aria-current="page"]')

    if (!nav || !active) return

    const hidden =
      active.offsetLeft < nav.scrollLeft ||
      active.offsetLeft + active.offsetWidth > nav.scrollLeft + nav.clientWidth

    if (hidden) nav.scrollLeft = active.offsetLeft - 16
  }, [state.tab, counted])

  return (
    <div className="flex items-center gap-1.5">
      {/* Its own scroller on a phone, bleeding to the left edge so the first
          pill lines up with the page and the row scrolls under the gutter. */}
      <nav
        ref={navRef}
        aria-label={t('submissions.tabs.label')}
        className="relative -my-1 -ml-4 min-w-0 flex-1 overflow-x-auto py-1 pl-4 [scrollbar-width:none] sm:ml-0 sm:pl-0"
      >
        <ul className="flex w-max gap-1.5 pb-0.5 sm:w-auto sm:flex-wrap">
          {tabsFor(isManager).map((tab) => {
            const active = state.tab === tab.slug
            const count = counts?.[tab.api]
            // Late work is the one count that should catch the eye. The word
            // on the tab says the same, so the colour is never all of it.
            const alarming = tab.slug === 'van-roka' && (count ?? 0) > 0

            return (
              <li key={tab.slug}>
                <Link
                  to={{ search: serializeQueue(updateQueue(state, { tab: tab.slug })).toString() }}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'min-h-control inline-flex items-center gap-1.5 rounded-xl px-3 text-sm font-medium whitespace-nowrap transition-colors',
                    'focus-visible:ring-ring/50 focus-visible:ring-3 focus-visible:outline-none',
                    active
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted/60 text-foreground/80 hover:bg-muted hover:text-foreground',
                  )}
                >
                  {t(`submissions.tab.${tab.slug}`)}
                  {count !== undefined && (
                    <span
                      className={cn(
                        'rounded-md px-1.5 text-xs tabular-nums',
                        active
                          ? 'bg-primary-foreground/20'
                          : alarming
                            ? 'bg-rose-500/15 text-rose-700 dark:text-rose-300'
                            : 'bg-background/70 text-muted-foreground',
                      )}
                    >
                      {formatNumber(count)}
                    </span>
                  )}
                </Link>
              </li>
            )
          })}
        </ul>
      </nav>

      <Hint>{t('submissions.tabs.hint')}</Hint>
    </div>
  )
}
